// src/products/capRaise/services/capRaiseApi.js
// API service layer for CapRaise module with enhanced error handling and debugging

import { supabase } from '../../../config';

class CapRaiseApiService {
  // Cache user to avoid repeated auth calls
  _userCache = null;
  _userCacheTime = 0;
  _cacheTimeout = 30000; // 30 seconds

  // Get authenticated user with caching
  async getAuthenticatedUser() {
    const now = Date.now();
    if (this._userCache && (now - this._userCacheTime) < this._cacheTimeout) {
      return this._userCache;
    }

    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        // Handle network errors gracefully
        if (error.message.includes('Failed to fetch') || error.message.includes('ERR_NETWORK')) {
          throw new Error('Network connection issue. Please check your internet connection and try again.');
        }
        throw error;
      }
      if (!user) throw new Error('User not authenticated');
      
      this._userCache = user;
      this._userCacheTime = now;
      return user;
    } catch (error) {
      // Clear cache on error to force re-authentication
      this.clearUserCache();
      throw error;
    }
  }

  // Clear user cache (call on logout)
  clearUserCache() {
    this._userCache = null;
    this._userCacheTime = 0;
  }
  // Normalize UI round type values to DB-accepted values
  normalizeRoundType(value) {
    if (!value) return 'seed';
    const lower = String(value).toLowerCase();
    const explicitMap = {
      'pre-seed': 'pre_seed',
      'seed': 'seed',
      'series-a': 'series_a',
      'series-b': 'series_b',
      'series-c': 'series_c',
      'bridge': 'bridge',
      'convertible': 'convertible',
      'growth': 'growth'
    };
    if (explicitMap[lower]) return explicitMap[lower];
    return lower.replace(/[^a-z0-9]+/g, '_');
  }

  // =============================================
  // ROUNDS MANAGEMENT  
  // =============================================

  /**
   * Create a new capital raising round
   */
  async createRound(roundData) {
    try {
      
      const user = await this.getAuthenticatedUser();

      
      // Validate required fields
      if (!roundData.company_id) {
        throw new Error('Company ID is required');
      }
      if (!roundData.round_name || !roundData.round_name.trim()) {
        throw new Error('Round name is required');
      }
      if (!roundData.target_amount || roundData.target_amount <= 0) {
        throw new Error('Target amount must be greater than 0');
      }

      // Clean and prepare data for database
      const cleanRoundData = {
        user_id: user.id,
        company_id: parseInt(roundData.company_id), // Ensure it's an integer
        round_name: roundData.round_name.trim(),
        round_type: this.normalizeRoundType(roundData.round_type),
        target_amount: parseFloat(roundData.target_amount),
        minimum_amount: roundData.minimum_amount ? parseFloat(roundData.minimum_amount) : null,
        maximum_amount: roundData.maximum_amount ? parseFloat(roundData.maximum_amount) : null,
        pre_money_valuation: roundData.pre_money_valuation ? parseFloat(roundData.pre_money_valuation) : null,
        post_money_valuation: roundData.post_money_valuation ? parseFloat(roundData.post_money_valuation) : null,
        share_price: roundData.share_price ? parseFloat(roundData.share_price) : null,
        description: roundData.description || null,
        notes: roundData.notes || null,
        dataroom_enabled: roundData.dataroom_enabled !== false, // Default to true
        auto_allocation: roundData.auto_allocation || false,
        oversubscription_allowed: roundData.oversubscription_allowed !== false, // Default to true
        maximum_oversubscription_percent: roundData.maximum_oversubscription_percent || 20.00,
        terms_config: roundData.terms_config || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };


      const { data, error } = await supabase
        .from('capraise_rounds')
        .insert(cleanRoundData)
        .select()
        .maybeSingle();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }


      // Log activity
      try {
        await this.logActivity(data.id, 'round_created', `Round "${data.round_name}" created`);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
        // Don't fail the whole operation for logging errors
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating round:', error);
      return { 
        data: null, 
        error: {
          message: error.message || 'Failed to create round',
          details: error
        }
      };
    }
  }

  /**
   * Update round data
   */
  async updateRound(roundId, updates) {
    try {
      
      const user = await this.getAuthenticatedUser();

      // Clean and prepare updates
      const cleanUpdates = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      // Normalize round_type if present
      if (cleanUpdates.round_type) {
        cleanUpdates.round_type = this.normalizeRoundType(cleanUpdates.round_type);
      }

      // Remove undefined values
      Object.keys(cleanUpdates).forEach(key => {
        if (cleanUpdates[key] === undefined) {
          delete cleanUpdates[key];
        }
      });

      const { data, error } = await supabase
        .from('capraise_rounds')
        .update(cleanUpdates)
        .eq('id', roundId)
        .eq('user_id', user.id) // Ensure user owns this round
        .select()
        .maybeSingle();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }


      // Log activity
      try {
        await this.logActivity(roundId, 'round_updated', 'Round details updated', updates);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update round error:', error);
      return { 
        data: null, 
        error: {
          message: error.message || 'Failed to update round',
          details: error
        }
      };
    }
  }

  /**
   * Get all rounds for a company
   */
  async getRounds(companyId = null) {
    try {
      const user = await this.getAuthenticatedUser();

      let query = supabase
        .from('capraise_rounds')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get rounds error:', error);
      return { data: [], error };
    }
  }

  /**
   * Get a specific round
   */
  async getRound(roundId) {
    try {
      const user = await this.getAuthenticatedUser();

      const { data, error } = await supabase
        .from('capraise_rounds')
        .select('*')
        .eq('id', roundId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Get round error:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete a round
   */
  async deleteRound(roundId) {
    try {
      const user = await this.getAuthenticatedUser();

      const { error } = await supabase
        .from('capraise_rounds')
        .delete()
        .eq('id', roundId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Log activity
      try {
        await this.logActivity(roundId, 'round_deleted', 'Round deleted');
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { error: null };
    } catch (error) {
      console.error('Delete round error:', error);
      return { error };
    }
  }

  // =============================================
  // INVESTORS MANAGEMENT
  // =============================================

  /**
   * Create a new investor
   */
  async createInvestor(investorData) {
    try {
      const { data, error } = await supabase
        .from('capraise_investors')
        .insert({
          ...investorData,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      // Log activity
      if (investorData.round_id) {
        await this.logActivity(investorData.round_id, 'investor_added', `Added investor: ${investorData.name}`);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Create investor error:', error);
      return { data: null, error };
    }
  }

  /**
   * Get investors for a round
   */
  async getInvestors(roundId) {
    try {
      const { data, error } = await supabase
        .from('capraise_investors')
        .select('*')
        .eq('round_id', roundId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get investors error:', error);
      return { data: [], error };
    }
  }

  /**
   * Update investor
   */
  async updateInvestor(investorId, updates) {
    try {
      const { data, error } = await supabase
        .from('capraise_investors')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', investorId)
        .select()
        .maybeSingle();

      if (error) throw error;

      // Log activity
      if (data && data.round_id) {
        await this.logActivity(data.round_id, 'investor_updated', `Updated investor: ${data.name}`);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Update investor error:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete an investor
   */
  async deleteInvestor(investorId) {
    try {
      const { data: investor } = await supabase
        .from('capraise_investors')
        .select('round_id, name')
        .eq('id', investorId)
        .maybeSingle();

      const { error } = await supabase
        .from('capraise_investors')
        .delete()
        .eq('id', investorId);

      if (error) throw error;

      if (investor) {
        await this.logActivity(investor.round_id, 'investor_removed', `Removed investor: ${investor.name}`);
      }

      return { error: null };
    } catch (error) {
      console.error('Delete investor error:', error);
      return { error };
    }
  }

  // =============================================
  // EMAIL TRACKING & TEMPLATES
  // =============================================

  /**
   * Track email sent to investors
   */
  async trackEmailSent(roundId, emailData) {
    try {
      const { data, error } = await supabase
        .from('capraise_email_tracking')
        .insert({
          round_id: roundId,
          ...emailData,
          sent_at: new Date().toISOString()
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      await this.logActivity(roundId, 'email_sent', `Email sent to ${emailData.recipient_email}`, emailData);
      return { data, error: null };
    } catch (error) {
      console.error('Track email error:', error);
      return { data: null, error };
    }
  }

  /**
   * Send invitation emails to multiple investors
   */
  async sendInvitationEmails(roundId, investorIds, emailData) {
    try {
      // This would integrate with actual email service
      
      // For now, just track that emails were "sent"
      const emailTracking = investorIds.map(investorId => ({
        round_id: roundId,
        investor_id: investorId,
        email_type: 'invitation',
        subject: emailData.subject,
        content: emailData.content,
        status: 'sent',
        sent_at: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('capraise_email_tracking')
        .insert(emailTracking)
        .select();

      if (error) throw error;

      await this.logActivity(roundId, 'bulk_emails_sent', 
        `Invitation emails sent to ${investorIds.length} investors`);
      
      return { data, error: null };
    } catch (error) {
      console.error('Send invitation emails error:', error);
      return { data: null, error };
    }
  }

  /**
   * Get email tracking data
   */
  async getEmailTracking(roundId) {
    try {
      const { data, error } = await supabase
        .from('capraise_email_tracking')
        .select('*')
        .eq('round_id', roundId)
        .order('sent_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get email tracking error:', error);
      return { data: [], error };
    }
  }

  /**
   * Get email templates
   */
  async getTemplates(type = null) {
    try {
      let query = supabase
        .from('capraise_templates')
        .select('*')
        .order('template_name');

      if (type) {
        query = query.eq('template_type', type);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Get templates error:', error);
      return { data: [], error };
    }
  }

  /**
   * Create email template
   */
  async createTemplate(templateData) {
    try {
      const user = await this.getAuthenticatedUser();

      const { data, error } = await supabase
        .from('capraise_templates')
        .insert({
          ...templateData,
          user_id: user.id,
          created_at: new Date().toISOString()
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create template error:', error);
      return { data: null, error };
    }
  }

  // =============================================
  // ACTIVITY LOGGING
  // =============================================

  /**
   * Log activity for audit trail
   */
  async logActivity(roundId, activityType, description, metadata = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('capraise_activity_log')
        .insert({
          round_id: roundId,
          user_id: user?.id,
          activity_type: activityType,
          description: description,
          metadata: metadata,
          created_at: new Date().toISOString()
        });

      if (error) {
        console.warn('Failed to log activity:', error);
      }
    } catch (error) {
      console.warn('Failed to log activity:', error);
      // Don't throw - logging failures shouldn't break main functionality
    }
  }

  /**
   * Get activity log for a round
   */
  async getActivityLog(roundId) {
    try {
      const { data, error } = await supabase
        .from('capraise_activity_log')
        .select('*')
        .eq('round_id', roundId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get activity log error:', error);
      return { data: [], error };
    }
  }

  // =============================================
  // UTILITY FUNCTIONS
  // =============================================

  /**
   * Calculate round progress percentage
   */
  calculateRoundProgress(round, investors = []) {
    if (!round || !round.target_amount) return 0;
    
    const committed = investors
      .filter(inv => ['committed', 'allocated'].includes(inv.status))
      .reduce((sum, inv) => sum + (inv.commitment_amount || inv.investment_amount || 0), 0);
    
    return Math.min((committed / round.target_amount) * 100, 100);
  }

  /**
   * Get investor status counts for analytics
   */
  getInvestorStatusCounts(investors = []) {
    return investors.reduce((counts, investor) => {
      const status = investor.status || 'prospect';
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    }, {});
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount, currency = 'USD') {
    // Import CurrencyService dynamically to avoid circular dependencies
    const CurrencyService = require('../../../services/currencyService').default;
    return CurrencyService.formatAmount(amount || 0, currency);
  }
}

// Export singleton instance
const capRaiseApi = new CapRaiseApiService();
export default capRaiseApi;